import { TApiSuccessCallback, TApiErrorCallback, IApiCall, ITransportError } from "./api.interfaces";
import { getServerDate } from "./storage/date";
import { isPNGSupported } from "./native/nativeFileApi";
import { JpegMimeType } from "./imageUtils";
import * as logger from "./logger";
import * as Sentry from "@sentry/browser";
import { getAddinNameFromUrl } from "./addinHelper";
import { LoginErrors } from "../modules/core/auth/helpers/errors";

export interface ICustomHeaders {
    name: string;
    value: string;
}

interface IBinaryUpload {
    mimeType: string;
    extension: string;
}

const BinaryUploadExtensions: ReadonlyMap<string, string> = new Map([
    [JpegMimeType, ".jpg"],
    ["image/png", ".png"]
]);

export class Transport {
    protected server: string;
    protected isSslDisabled = false;
    protected isDebug = false;
    protected timeout: number;
    protected isOnline = true;
    protected changeNetworkStatusCallback: (isOnline: boolean) => void;
    protected isApiCallLoggingEnabled = false;

    protected customHeaders: ICustomHeaders[] = [];

    constructor(server: string) {
        this.server = server;
    }

    set isLoggingEnabled(_: boolean) {
        //todo: modify method when improving logger.
        this.isApiCallLoggingEnabled = false;
    }

    call(_1: string, _2: any, _3: TApiSuccessCallback, _4: TApiErrorCallback, _5?: ArrayBuffer, _6 = false, _7?: string): IApiCall {
        throw new Error("Not implemented yet");
    }

    setServer(server: string) {
        this.server = server;
    }

    getServer() {
        return this.server;
    }

    setIsSslDisabled(isSslDisabled: boolean) {
        this.isSslDisabled = isSslDisabled;
    }

    setTimeout(timeoutInSeconds: number) {
        this.timeout = timeoutInSeconds;
    }

    setIsDebug(isDebug: boolean) {
        this.isDebug = isDebug;
    }

    changeIsOnline(isSuccessful) {
        if (isSuccessful !== this.isOnline) {
            this.isOnline = isSuccessful;
            this.changeNetworkStatusCallback && this.changeNetworkStatusCallback(this.isOnline);
        }
    }

    getIsOnline() {
        return this.isOnline;
    }

    onChangeNetworkStatus(changeNetworkStatusCallback) {
        this.changeNetworkStatusCallback = changeNetworkStatusCallback;
    }

    setHeaders(headers: ICustomHeaders[]) {
        this.customHeaders = headers;
    }

    protected getCallUrl (method?: string): string {
        let thisServer = this.server.replace(/\S*:\/\//, "").replace(/\/$/, "");
        return `${ (this.isSslDisabled ? "http://" : "https://") }${ thisServer }/apiv1${ (method ? `/${ method }` : "") }`;
    }

    protected isJsonContentType(contentType: string): boolean {
        let mediaType = contentType.split(";")[0].trim().toLowerCase();
        return mediaType === "application/json" || mediaType.endsWith("+json");
    }

    protected debugLog (...rest) {
        if (this.isDebug) {
            logger.log(logger.Source.DEBUG, [getServerDate(), ...rest]);
        }
    }

    protected handleError (error, errorCallback, apiCall?, retryTime?) {
        if (error && error.data && error.data.type) {
            if (error.data.type ===  "DbUnavailable") {
                error.message = "Database is not available.";
            }
        }
        if (this.isDebug) {
            logger.error(logger.Source.DEBUG, `${ error.message }, ${ error }`);
        }
        if (error && apiCall) {
            let apiCallObj = apiCall;
            if (typeof (apiCallObj) === "string") {
                try {
                    apiCallObj = JSON.parse(apiCallObj);
                } catch (e) {}
            }
            error.data = error.data || {};
            error.data.info = error.data.info || {};
            if (typeof (error.data.info) === "string") {
                try {
                    error.data.info = JSON.parse(error.data.info);
                } catch {}
            }
            if (typeof (error.data.info) === "object") {
                error.data.info.apiCall = this.clearPersonalInformation(apiCallObj);
            }
        }
        if (error && error.data && retryTime !== undefined) {
            error.data.retry = parseInt(retryTime);
        }

        if (errorCallback) {
            errorCallback(error);
        }
    }

    protected clearPersonalInformation(params) {
        if (params) {
            Object.keys(params).forEach((key) => {
                if (key === "credentials") {
                    delete params[key];
                } else {
                    if (key.toLowerCase().indexOf("password") > -1 && typeof (params[key]) === "string") {
                        params[key] = params[key].replace(/./g, "*");
                    }
                    if (typeof (params[key]) === "object") {
                        this.clearPersonalInformation(params[key]);
                    }
                }
            });
        }
        return params;
    }

    protected getError(message, type, info, statusCode?): ITransportError {
        return {
            message: message,
            code: statusCode || -1,
            data: {
                id: "",
                type: type,
                requestIndex: 0,
                info: info
            },
            errors: [{
                name: type,
                message: message,
                stack: ""
            }]
        };
    }

    protected getCancelledError() {
        return this.getError("Cancelled", "NetworkError", {});
    }

    protected getJSError(e) {
        return this.getError(e.message, "InvalidResponse", { e: e });
    }
}

function getCallNameString(method: string, params: any) {
    return `${ method ?? "UnknownMethod" }${ params?.typeName ? `<${ params.typeName }>` : "" }`;
}

export function getCallLoggingMessage(method: string, params: any) {
    let callNameString: string;
    if (method === "ExecuteMultiCall" && params?.calls?.length > 0) {
        const callHash = (params.calls as any[]).reduce((acc, val) => {
            callNameString = getCallNameString(val.method, val.params);
            acc[callNameString] = (acc[callNameString] ?? 0) + 1;
            return acc;
          }, {} as {[callName: string]: number});
        const calls: string[] = [];
        for (const key in callHash) {
            if (callHash.hasOwnProperty(key)) {
                calls.push(`${ key }(${ callHash[key] })`);
            }
        }
        return `EMC[${ calls.join(",") }]`;
    }
    callNameString = getCallNameString(method, params);
    return `${ callNameString }`;
}

export class XHRTransport extends Transport {

    private readonly expectBinaryData = ["DownloadMediaFile", "GetStaticMap", "GetColdChainDeliveryProofPdf"];

    call(
        method: string,
        params: any,
        callbackSuccess: TApiSuccessCallback,
        callbackError: TApiErrorCallback,
        binaryData?: ArrayBuffer,
        ignoreCredentials = false,
        binaryMimeType?: string
    ): IApiCall {
        let rpcString;
        try {
            rpcString = JSON.stringify({
                method: method || "",
                params: params
            });
        } catch (e) {
            this.handleError(this.getJSError(e), callbackError);
            return { abort: () => {} };
        }

        let binaryUpload: IBinaryUpload | undefined;
        if (binaryData) {
            const mimeType = binaryMimeType ?? (isPNGSupported() ? "image/png" : JpegMimeType);
            const extension = BinaryUploadExtensions.get(mimeType);
            // The server rejects a file whose extension differs from its registered MediaFile name, so never guess one.
            if (!extension) {
                logger.error(logger.Source.API, `${ method }: unsupported MIME type "${ mimeType }" — binary upload not sent`);
                this.handleError(this.getError(`Unsupported binary MIME type "${ mimeType }"`, "InvalidRequest", {}), callbackError);
                return { abort: () => {} };
            }
            binaryUpload = { mimeType, extension };
        }

        let expectedBinaryData = this.expectBinaryData.indexOf(method) !== -1;
        let xhr = new XMLHttpRequest();
        let pageName = window.location.hash.match(/^#(.*?)(,(.*))?$/);
        const methodNameForAPM = getCallLoggingMessage(method, params);

        // Start a Sentry span for the API call only if tracing is enabled
        let span: Sentry.Span | undefined;
        if (window.errorTracker?.isTracingAllowed) {
            span = Sentry.startSpanManual({
                op: "http.client",
                name: `API: ${ methodNameForAPM }`,
                attributes: {
                    "http.method": "POST",
                    "http.url": this.getCallUrl(),
                    "rpc.method": methodNameForAPM
                }
            }, (sentrySpan) => sentrySpan);
        }

        xhr.open("POST", this.getCallUrl(), true);
        if (!binaryData) {
            xhr.setRequestHeader("Content-Type", "application/json");
        }
        if (this.customHeaders.length) {
            this.customHeaders.forEach(customHeader => {
                if (customHeader.name === "Authorization" && ignoreCredentials) {
                    return;
                }

                xhr.setRequestHeader(customHeader.name, encodeURI(customHeader.value));
            });
        }
        if (pageName) {
            if (pageName[1] === "addin") {
                /*
                    Setting a meaningful "Page-name" header when the user is on an Addin page requires a bit more work
                    because of the way we handle navigation to Addin pages. The URL state for showing addin pages are in
                    the form "https://my.geotab.com/<db name>/drive/#addin,(addinName:<hash>)", where the <hash> value is the result of
                    calling "window.btoa" on a value like "Addins/<addinName>/DriveAppLink". So, we can use Regex to extract the hashed value
                    and convert it back to grab the addin name, which we can provide as the "Page-name" header when making API calls.

                    Note: the regex is structured to grab all of the characters after "addinName:" and the next instance of either a closing
                    bracket character, or a comma (in the case that we have more URL parameters). This is acceptable because "btoa" & "atob"
                    only operate with Base64 encoding, and thus only support the characters
                    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/"
                    https://developer.mozilla.org/en-US/docs/Web/API/atob
                    https://developer.mozilla.org/en-US/docs/Web/API/btoa
                    https://developer.mozilla.org/en-US/docs/Glossary/Base64
                */
                let url = window.location.hash;
                let addinName = getAddinNameFromUrl(url);
                if (addinName) {
                    xhr.setRequestHeader("Page-name", `addin-${ addinName }`);
                }
            } else {
                xhr.setRequestHeader("Page-name", pageName[1]);
            }
        }
        xhr.setRequestHeader("X-Application-Name", "Drive");
        xhr.addEventListener("abort", () => {
            if (callbackError) {
                callbackError(this.getCancelledError());
            }
        });

        if (expectedBinaryData) {
            xhr.responseType = "blob";
        }

        xhr.onreadystatechange = () => {
            if (xhr.readyState === 4) {
                // End the Sentry span
                span?.end();

                if (xhr.status === 200) {
                    let data;
                    let error;
                    let result;

                    let contentType = xhr.getResponseHeader("Content-Type");
                    if (!expectedBinaryData && contentType && !this.isJsonContentType(contentType)) {
                        // Server returned a non-JSON response (e.g. auth proxy login redirect, gateway error page)
                        this.debugLog(method, "ERROR", { status: xhr.status });
                        this.handleError(this.getError("Server returned a non-JSON response", "InvalidResponse", { xhr: xhr }), callbackError, rpcString || null);
                        return;
                    }

                    try {
                        data = !expectedBinaryData ? JSON.parse(xhr.responseText) : xhr.response;
                    } catch (e) {
                        this.handleError(this.getJSError(e), callbackError, rpcString || null);
                    }

                    if (data && data.error) {
                        error = data.error;
                        let retryTime = xhr.getResponseHeader("retry-after");
                        this.debugLog(method, "ERROR", error);
                        this.handleError(error, callbackError, rpcString || null, retryTime);
                    } else if (!data) {
                        this.debugLog(method, "ERROR", xhr);
                        this.handleError(this.getXHRError(xhr), callbackError, rpcString || null);
                    } else {
                        if (expectedBinaryData) {
                            result = data;
                        } else {
                            result = typeof (data.result) === "undefined" || data.result === null ? {} : data.result;
                        }
                        this.debugLog(method, "SUCCESS", { result: result });
                        this.changeIsOnline(true);
                        callbackSuccess(result);
                    }
                } else {
                    this.debugLog(method, "ERROR", xhr);

                    if (xhr.status === 0) {
                        this.changeIsOnline(false);
                    }
                    if (xhr.status === 403) {
                        logger.error(logger.Source.API, `403 Error: ${ JSON.stringify({
                            method,
                            params: params?.typeName
                        }) }`);
                    }
                    this.handleError(this.getXHRError(xhr), callbackError, rpcString || null);
                }
            }
        };

        if (this.timeout > 0) {
            xhr.timeout = this.timeout * 1000;
        }

        if (binaryData && binaryUpload) {
            let formData = new FormData();
            const name = `test${ binaryUpload.extension }`;
            formData.append("JSON-RPC", encodeURIComponent(rpcString));
            formData.append(name, new Blob([binaryData], { type: binaryUpload.mimeType }), name);
            xhr.send(formData);
        } else {
            xhr.send(rpcString);
        }

        return {
            abort: function () {
                xhr.abort();
            }
        };
    }

    getXHRError(xhr) {
        let message;

        switch (xhr.status) {
            case 0:
                message = LoginErrors.CONNECTION_REFUSED_MSG;
                break;
            case 400:
                message = "Bad request.";
                break;
            case 401:
                return this.getError("Unauthorized", "InvalidCredentialsError", { xhr }, xhr.status);
            case 403:
                message = "[403] Forbidden.";
                break;
            case 404:
                message = "[404] Server not found.";
                break;
            case 408:
                message = "Request timeout.";
                break;
            default:
                message = "A server issue has occurred. Please try again in a few minutes.";
                break;
        }

        return this.getError(message, "NetworkError", { xhr: xhr }, xhr.status || -1);
    }
}

export class NoConnectionTransport extends XHRTransport {
    call(method: string, params: any, _1: TApiSuccessCallback, callbackError: TApiErrorCallback): IApiCall {
        let xhr = new XMLHttpRequest();
        let rpcString;
        xhr.open("POST", this.getCallUrl(), true);
        xhr.setRequestHeader("Content-Type", "application/json");
        xhr.addEventListener("abort", () => {
            if (callbackError) {
                callbackError(this.getCancelledError());
            }
        });
        xhr.onreadystatechange = () => {
            if (xhr.readyState === 4) {
                this.changeIsOnline(false);
                this.handleError(this.getXHRError(xhr), callbackError, rpcString || null);
            }
        };

        try {
            rpcString = JSON.stringify({
                method: `${ method || "" }<**noConnectionSimulation**>`,
                params: params
            });
        } catch (e) {
            this.handleError(this.getJSError(e), callbackError);
            return { abort: () => {} };
        }

        if (this.timeout > 0) {
            xhr.timeout = this.timeout * 1000;
        }

        xhr.send(rpcString);
        xhr.abort();

        return {
            abort: function () {
                xhr.abort();
            }
        };
    }
}